import { Box } from '@mui/material';
import { FC, useRef } from 'react';

import { useActiveDragCard, useDragCount } from './BoardInteractionContext';
import { DispatchCardView } from './DispatchCardView';
import { DisplayMetric } from './DisplayMetricToggle';

export type DragOverlayCardProps = {
  displayMetrics: Set<DisplayMetric>;
};

/**
 * DragOverlay (@dnd-kit/core) の中身。掴んでいるカードと複数ドラッグの件数バッジを描く。
 *
 * Why not Presenter の JSX に直接置く: activeCard / dragCount を Presenter の state に
 * 持つと、ドラッグの開始・終了のたびに Presenter 全体が再描画され、そこから全カラムへ
 * 波及する。ここだけが store を購読する子になることで、描き直すのはこの 1 枚だけになる。
 *
 * ドラッグ中でないときこの関数は呼ばれない。DragOverlay は active が無い間 children を
 * PositionedOverlay ごと描かないためである
 * （node_modules/@dnd-kit/core/dist/core.cjs.development.js:3947 の
 * `active && key ? PositionedOverlay : null`）。例外はドロップ後のアニメーション中で、
 * それが下の lastCardRef / lastCountRef が必要な理由である。
 */
export const DragOverlayCard: FC<DragOverlayCardProps> = ({ displayMetrics }) => {
  const activeCard = useActiveDragCard();
  const dragCount = useDragCount();

  // Why not activeCard が null なら即 return null: dnd-kit の AnimationManager は children が
  // null になった commit でも、直前の要素を cloneElement して描き続ける
  // （同 3584-3607）。それが「掴んだカードが元の位置へ飛んで戻る」250ms の
  // ドロップアニメーションの実体で、その間 default の sideEffects は元のカードを
  // opacity: 0 にする（同 3740-3750）。clone は key を保つので React はこの component の
  // fiber を使い回し、購読が endDrag を拾ってここが再描画される。null を返すと
  // オーバーレイが空になり、掴んだノードが生き残るケース（ESC キャンセル・ドロップ先
  // なし・同位置への並び替え等）でカードが 250ms 消えて戻って見える。
  //
  // 件数バッジも同じ理由で覚える。endDrag は dragCount も 0 に戻すので、覚えないと
  // 未割当の複数ドラッグを ESC で戻したときにバッジだけ先に消える。
  const lastCardRef = useRef(activeCard);
  const lastCountRef = useRef(dragCount);
  if (activeCard) {
    lastCardRef.current = activeCard;
    lastCountRef.current = dragCount;
  }
  // Why not 「次のドラッグで activeCard が決まらず前回のカードが出る」を心配する:
  // dnd-kit は登録済みの draggable でしかドラッグを開始せず、この DndContext に登録される
  // のは行のカード（DispatchCardView の useSortable）と未割当アイテム
  // （DraggableCardItem の useDraggable）だけである。行のカードのうち掴めないものは
  // id に -nodrag を付けたうえで disabled: true なのでドラッグが始まらず、掴めるものの id は
  // handleDragStart が引く cardLookup に必ず居る（Board が描く visibleRowGroups は
  // cardLookup の元になる rowGroups の部分集合）。未割当リストへ渡している items も
  // handleDragStart が探すのと同じ配列（unassigned）である。よってこの経路には入らない。
  const card = activeCard ?? lastCardRef.current;
  const count = activeCard ? dragCount : lastCountRef.current;

  if (!card) return null;

  return (
    <Box sx={{ position: 'relative' }}>
      <DispatchCardView card={card} isDragging displayMetrics={displayMetrics} />
      {count > 1 && (
        <Box
          component="span"
          sx={{
            position: 'absolute',
            right: -8,
            top: -8,
            display: 'flex',
            height: 20,
            width: 20,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '9999px',
            bgcolor: 'hsl(var(--primary))',
            fontSize: 10,
            fontWeight: 700,
            color: 'hsl(var(--primary-foreground))',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
          }}
        >
          {count}
        </Box>
      )}
    </Box>
  );
};
